import React, { useState, useEffect, Suspense } from 'react';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { Box, Alert, Button, Typography, CircularProgress } from '@mui/material';
import MainMenu from './components/MainMenu';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/Login';
import { SignUp } from './components/SignUp';
import { ForgotPassword } from './components/ForgotPassword';
import { ResetPassword } from './components/ResetPassword';
import { LicenseProvider, useLicense } from "./contexts/LicenseContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LicenseCheck from './components/LicenseCheck';
import PermissionGuard from './components/PermissionGuard';
import { useValidation } from './contexts/ValidationContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { decrypt } from './utils/encryption';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { CookieConsent } from './components/CookieConsent';
import { ValidationProvider } from './contexts/ValidationContext';
import { BatchProvider } from './contexts/BatchContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { RecipeProvider } from './contexts/RecipeContext';
import { BreweryProvider } from './contexts/BreweryContext';
import { ScrollToTop } from './components/ScrollToTop';
import { UserSettingsProvider } from './contexts/UserSettingsContext';

// Route-level code splitting: each view loads on first visit instead of
// shipping the whole app (charts, calendar, label editor, ...) in one bundle.
const StockPlan = lazy(() => import('./components/StockPlan'));
const BatchPlan = lazy(() => import('./components/BatchPlan'));
const Inventory = lazy(() => import('./components/Inventory'));
const Statistics = lazy(() => import('./components/Statistics').then(m => ({ default: m.Statistics })));
const BatchSearch = lazy(() => import('./components/BatchSearch').then(m => ({ default: m.BatchSearch })));
const Settings = lazy(() => import('./components/Settings'));
const Help = lazy(() => import('./components/Help'));
const Contact = lazy(() => import('./components/Contact'));
const PurchaseComplete = lazy(() => import('./components/PurchaseComplete'));
const TapList = lazy(() => import('./components/TapList'));
const BatchLabels = lazy(() => import('./components/BatchLabels'));
const Tools = lazy(() => import('./components/Tools'));
const YeastBank = lazy(() => import('./components/YeastBank'));
const RankList = lazy(() => import('./components/RankList').then(m => ({ default: m.RankList })));
const Competitions = lazy(() => import('./components/Competitions'));
const Compare = lazy(() => import('./components/Compare'));
const Recipes = lazy(() => import('./components/Recipes'));
const RecipeView = lazy(() => import('./components/RecipeView'));
const BreweryUsers = lazy(() => import('./components/BreweryUsers'));
const ProductionReport = lazy(() => import('./components/ProductionReport'));
const QualityControl = lazy(() => import('./components/QualityControl'));
const PurchaseOrders = lazy(() => import('./components/PurchaseOrders'));
const MaltLots = lazy(() => import('./components/MaltLots'));
const YeastLots = lazy(() => import('./components/YeastLots'));
const BrewDay = lazy(() => import('./components/BrewDay'));
const AuditLog = lazy(() => import('./components/AuditLog'));

function AppContent() {
  const { canFetchBatches } = useValidation();
  const { breweryId, isBreweryAdmin, isBreweryMember } = useLicense();
  const [apiKey, setApiKey] = useState<string>('');
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [apiKeyRefreshTrigger, setApiKeyRefreshTrigger] = useState(0);

  const isBreweryAccount = isBreweryAdmin || isBreweryMember;
  const effectiveApiKey = canFetchBatches ? apiKey : '';

  // Always wrap with BatchProvider, but only pass apiKey when validation passes.
  // Brewfather data flow is IDENTICAL for homebrewers and brewery accounts with
  // an API key: the production-proven client fetch (API + localStorage cache).
  // The Firestore path is only for brewery MEMBERS, who have no API key of
  // their own and read the data the brewerySync Cloud Function mirrors.
  // Don't decide the path while the stored API key is still loading/decrypting:
  // the empty-key window would briefly mount the Firestore listeners and then
  // flip to the client path when the key arrives (wasted reads + remounts).
  const useFirestorePath = isBreweryAccount && !effectiveApiKey && !apiKeyLoading;
  const dataProviders = (
    <BatchProvider apiKey={effectiveApiKey} breweryId={useFirestorePath ? breweryId : null}>
      <InventoryProvider apiKey={effectiveApiKey} breweryId={useFirestorePath ? breweryId : null}>
        <RecipeProvider apiKey={effectiveApiKey} breweryId={useFirestorePath ? breweryId : null}>
          <AppContentInner
            apiKey={apiKey}
            apiKeyLoading={apiKeyLoading}
            apiKeyRefreshTrigger={apiKeyRefreshTrigger}
            setApiKey={setApiKey}
            setApiKeyLoading={setApiKeyLoading}
            setApiKeyRefreshTrigger={setApiKeyRefreshTrigger}
            canFetchBatches={canFetchBatches}
          />
        </RecipeProvider>
      </InventoryProvider>
    </BatchProvider>
  );

  // Wrap with BreweryProvider if user has a brewery
  if (breweryId) {
    return (
      <BreweryProvider>
        {dataProviders}
      </BreweryProvider>
    );
  }

  return dataProviders;
}

interface AppContentInnerProps {
  apiKey: string;
  apiKeyLoading: boolean;
  apiKeyRefreshTrigger: number;
  setApiKey: (key: string) => void;
  setApiKeyLoading: (loading: boolean) => void;
  setApiKeyRefreshTrigger: (trigger: number) => void;
  canFetchBatches: boolean | null;
}

function AppContentInner({
  apiKey,
  apiKeyLoading,
  apiKeyRefreshTrigger,
  setApiKey,
  setApiKeyLoading,
  setApiKeyRefreshTrigger,
  canFetchBatches
}: AppContentInnerProps) {
  const [, setIsMenuOpen] = useState(true);
  const [apiKeyLoadFailed, setApiKeyLoadFailed] = useState(false);
  const { user, loading } = useAuth();
  const {
    loading: licenseLoading, licenseKey, licenseKeyLoading, licenseKeyError, refreshLicenseKey,
    isBreweryAdmin, isBreweryMember, breweryId,
  } = useLicense();
  const isBreweryAccount = isBreweryAdmin || isBreweryMember;
  // Only try the clear-cache-and-refetch recovery once per session to avoid loops
  const decryptRetriedRef = React.useRef(false);

  // Load API key from Firestore when user changes or when refresh is triggered
  useEffect(() => {
    let cancelled = false;
    const loadApiKey = async () => {
      if (!user?.uid) {
        setApiKey('');
        setApiKeyLoading(false);
        return;
      }

      // Brewery members don't need their own API key
      if (isBreweryMember && breweryId) {
        setApiKey('');
        setApiKeyLoadFailed(false);
        setApiKeyLoading(false);
        return;
      }

      // Encryption key still resolving (license status or key fetch in flight) — wait
      if (licenseKeyLoading) {
        setApiKeyLoading(true);
        return;
      }

      if (!licenseKey) {
        // Terminal: no subscription (nothing to decrypt with) — or the key fetch
        // failed, which renders as a retryable error instead of "not configured"
        setApiKey('');
        setApiKeyLoadFailed(!!licenseKeyError);
        setApiKeyLoading(false);
        return;
      }

      try {
        setApiKeyLoading(true);
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (cancelled) return;

        const storedApiKey = docSnap.exists() ? docSnap.data()?.apiKey : null;
        if (!storedApiKey) {
          setApiKey('');
          setApiKeyLoadFailed(false);
          setApiKeyLoading(false);
          return;
        }

        try {
          setApiKey(decrypt(storedApiKey, licenseKey));
          setApiKeyLoadFailed(false);
        } catch (decryptErr) {
          // A stored key that no longer decrypts usually means the cached encryption
          // key is stale (e.g. subscription changed). Refetch the key once and retry.
          if (!decryptRetriedRef.current) {
            decryptRetriedRef.current = true;
            console.warn('Stored API key failed to decrypt — refreshing encryption key and retrying');
            const freshKey = await refreshLicenseKey();
            if (cancelled) return;
            if (freshKey) {
              setApiKey(decrypt(storedApiKey, freshKey));
              setApiKeyLoadFailed(false);
              setApiKeyLoading(false);
              return;
            }
          }
          throw decryptErr;
        }
        setApiKeyLoading(false);
      } catch (err) {
        console.error('Error loading API key:', err);
        if (cancelled) return;
        setApiKey('');
        setApiKeyLoadFailed(true);
        setApiKeyLoading(false);
      }
    };

    loadApiKey();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, licenseKey, licenseKeyLoading, licenseKeyError, refreshLicenseKey, apiKeyRefreshTrigger, isBreweryMember, breweryId]);

  // Callback function to trigger API key refresh
  const refreshApiKey = () => {
    setApiKeyRefreshTrigger(apiKeyRefreshTrigger + 1);
  };

  // Retry after a transient failure: refetch the encryption key too if that was
  // what failed, then re-run the API key load
  const retryApiKeyLoad = async () => {
    if (licenseKeyError) {
      await refreshLicenseKey();
    }
    refreshApiKey();
  };

  const renderApiKeyError = () => (
    <Box sx={{ p: 3 }}>
      {apiKeyLoadFailed ? (
        <Alert severity="warning">
          <Typography variant="body1" gutterBottom>
            Could not load your BrewFather API key. Please check your connection and try again.
          </Typography>
          <Button
            onClick={retryApiKeyLoad}
            color="primary"
            size="small"
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : (
        <Alert severity="error">
          <Typography variant="body1" gutterBottom>
            BrewFather API key is not configured or invalid. Please go to Settings to configure your API key.
          </Typography>
          <Button
            component={Link}
            to="/settings"
            color="primary"
            size="small"
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Go to Settings
          </Button>
        </Alert>
      )}
    </Box>
  );

  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  // First check: Are we still determining authentication?
  if (loading) {
    return renderLoading();
  }

  // Second check: No user = show auth routes only
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* Public: the user is signed out and arriving from a reset email */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Third check: User exists, are other checks still loading?
  // Brewery members skip API key loading since they don't need one
  const isOtherChecksLoading = (isBreweryMember && breweryId)
    ? false
    : (apiKeyLoading || canFetchBatches === null || licenseLoading);
  
  if (isOtherChecksLoading) {
    return renderLoading();
  }

  return (
    <MainMenu onMenuToggle={setIsMenuOpen}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <ErrorBoundary scope="routes">
          <Suspense fallback={renderLoading()}>
          <Routes>
          <Route
            path="/stock-plan"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="stock-plan"><StockPlan apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/batch-plan"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="batch-plan"><BatchPlan apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/inventory"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="inventory"><Inventory apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/statistics"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="statistics"><Statistics apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/batch-search"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="batch-search"><BatchSearch apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/compare"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="compare"><Compare apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/rank-list"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="rank-list"><RankList apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/competitions"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  <PermissionGuard viewKey="competitions"><Competitions /></PermissionGuard>
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/tap-list"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="tap-list"><TapList apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/batch-labels"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="batch-labels"><BatchLabels apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/yeast-bank"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  {(apiKey || isBreweryAccount) ? <PermissionGuard viewKey="yeast-bank"><YeastBank apiKey={apiKey} /></PermissionGuard> : renderApiKeyError()}
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/tools"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  <PermissionGuard viewKey="tools"><Tools /></PermissionGuard>
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/recipes"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  <PermissionGuard viewKey="recipes"><Recipes apiKey={apiKey} /></PermissionGuard>
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          <Route
            path="/recipes/:id"
            element={
              <LicenseCheck>
                <ProtectedRoute>
                  <PermissionGuard viewKey="recipes"><RecipeView apiKey={apiKey} /></PermissionGuard>
                </ProtectedRoute>
              </LicenseCheck>
            }
          />
          {isBreweryAccount && (
            <Route
              path="/production-report"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="production-report"><ProductionReport /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAccount && (
            <Route
              path="/quality"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="quality"><QualityControl /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAccount && (
            <Route
              path="/purchase-orders"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="purchase-orders"><PurchaseOrders /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAccount && (
            <Route
              path="/brew-day"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="brew-day"><BrewDay /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAccount && (
            <Route
              path="/malt-lots"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="malt-lots"><MaltLots /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAccount && (
            <Route
              path="/yeast-lots"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <PermissionGuard viewKey="yeast-lots"><YeastLots /></PermissionGuard>
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAdmin && (
            <Route
              path="/audit-log"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <AuditLog />
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          {isBreweryAdmin && (
            <Route
              path="/brewery-users"
              element={
                <LicenseCheck>
                  <ProtectedRoute>
                    <BreweryUsers />
                  </ProtectedRoute>
                </LicenseCheck>
              }
            />
          )}
          <Route path="/settings" element={<Settings refreshApiKey={refreshApiKey} />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/purchase-complete" element={<PurchaseComplete />} />
          <Route path="/" element={<Navigate to="/batch-plan" replace />} />
          <Route path="*" element={<Navigate to="/batch-plan" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </Box>
    </MainMenu>
  );
}

function App() {
  return (
    <ErrorBoundary scope="app">
      <Router basename="/">
        <ScrollToTop />
        <ThemeProvider>
          <AuthProvider>
            <UserSettingsProvider>
              <LicenseProvider>
                <ValidationProvider>
                  <AnalyticsProvider>
                    <AppContent />
                    <CookieConsent />
                  </AnalyticsProvider>
                </ValidationProvider>
              </LicenseProvider>
            </UserSettingsProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;